import React from 'react';
import { useNavigate } from 'react-router-dom';
import './EditorHeader.css';

const EditorHeader = ({
  template,
  onBack,
  editingName,
  nameValue,
  nameSaving,
  onNameChange,
  onNameEditStart,
  onNameEditCancel,
  onNameSubmit,
  showOcrWords,
  showRegions,
  showAdvanced,
  showZoneTypeField,
  showZoneTypeAnchor,
  showZoneTypeRegion,
  showZoneTypeTable,
  showAnchorRegion,
  onToggleOcrWords,
  onToggleRegions,
  onToggleAdvanced,
  onToggleZoneTypeField,
  onToggleZoneTypeAnchor,
  onToggleZoneTypeRegion,
  onToggleZoneTypeTable,
  onToggleAnchorRegion,
  onTestRun,
  testRunLoading,
  onDetectRegions,
  detectingRegions,
  onSave,
  saving,
  isDirty,
  onDiscard,
  onPublish,
  onUnpublish,
  publishing,
  isPublished,
  hasUnpublishedChanges,
  canBuild,
  docTypeSelector,
}) => {
  // Navigation to the template-settings page (page selection, identifiers,
  // anchors). Route is RequireBuilder-gated in App.js and this editor is only
  // reachable by builders, so no extra permission logic here.
  const navigate = useNavigate();
  // Two rows (EditorHeader.css): name and actions on the first, document type
  // and view toggles on the second. Every action button stays on screen at
  // 1280px; the name truncates to one line instead of wrapping.
  return (
  <div className="editor-header editor-header-two-row">
    <div className="editor-header-row editor-header-primary">
      <div className="header-left">
        <button onClick={onBack} className="btn btn-secondary">
          ← Back to Templates
        </button>
        <button
          onClick={() => navigate(`/template-settings/${template?.id}`)}
          className="btn btn-secondary"
          disabled={!template?.id}
          title="Template settings — page selection, identifiers, anchors"
        >
          ⚙ Settings
        </button>
        <div className="template-info">
          {editingName ? (
            <form
              className="template-name-edit-form"
              onSubmit={(e) => {
                e.preventDefault();
                onNameSubmit();
              }}
            >
              <input
                className="template-name-input"
                type="text"
                value={nameValue}
                onChange={(e) => onNameChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') onNameEditCancel(); }}
                onBlur={() => {
                  const trimmed = nameValue.trim();
                  if (trimmed && trimmed !== template?.name) {
                    onNameSubmit();
                  } else {
                    onNameEditCancel();
                  }
                }}
                autoFocus
                disabled={nameSaving}
                maxLength={200}
              />
              {nameSaving && <span className="template-name-saving">Saving…</span>}
            </form>
          ) : (
            <h1
              className="template-name-editable"
              onClick={onNameEditStart}
              title={`${template?.name || 'Untitled Template'} (click to rename)`}
            >
              {template?.name || 'Untitled Template'}
              <span className="template-name-edit-icon">✏️</span>
            </h1>
          )}
          {template?.description && (
            <p className="template-description" title={template.description}>{template.description}</p>
          )}
        </div>
      </div>

      <div className="header-actions">
        <button
          onClick={onTestRun}
          disabled={testRunLoading || !template?.source_document_id}
          className="btn btn-test-run"
          title={!template?.source_document_id ? 'Upload a sample document first' : 'Run extraction test'}
        >
          {testRunLoading ? '⏳ Running…' : '▶ Test Run'}
        </button>
        <button
          onClick={onDetectRegions}
          disabled={detectingRegions || !template?.source_document_id}
          className="btn btn-secondary"
          title="Detect document regions using AI"
        >
          {detectingRegions ? '⏳ Detecting…' : '🔍 Detect Regions'}
        </button>
        <button
          onClick={onSave}
          disabled={saving || !isDirty}
          className="btn btn-primary"
        >
          {saving ? 'Saving…' : isDirty ? 'Save Changes' : 'Saved'}
        </button>
        {isDirty && (
          <button onClick={onDiscard} className="btn btn-secondary btn-sm">
            Discard
          </button>
        )}
        {/* 2026-06-14 B-2: publish & unpublish are builder actions (admin||
            power_user; server enforces via accessPolicy buildOnly). The whole
            template editor is builder-only now (route-guarded), and the server
            gates every template write on buildOnly. */}
        {canBuild && (isPublished ? (
          <button
            onClick={hasUnpublishedChanges ? onPublish : onUnpublish}
            disabled={publishing || isDirty}
            className={`btn btn-sm ${hasUnpublishedChanges ? 'btn-publish' : 'btn-secondary'}`}
          >
            {publishing ? '…' : hasUnpublishedChanges ? 'Publish' : 'Unpublish'}
          </button>
        ) : (
          <button
            onClick={onPublish}
            disabled={publishing || isDirty}
            className="btn btn-sm btn-publish"
          >
            {publishing ? 'Publishing…' : 'Publish'}
          </button>
        ))}
      </div>
    </div>

    <div className="editor-header-row editor-header-secondary">
      {docTypeSelector}
      <div className="header-center">
        <div className="view-toggles">
          <label className="toggle-label" title="Show/hide OCR word boxes on document">
            <input type="checkbox" checked={showOcrWords} onChange={onToggleOcrWords} />
            <span>OCR</span>
          </label>
          <label className="toggle-label" title="Show/hide detected regions">
            <input type="checkbox" checked={showRegions} onChange={onToggleRegions} />
            <span>Regions</span>
          </label>
          <label className="toggle-label" title="Draw anchor and region zones at full strength (they are dimmed otherwise)">
            <input type="checkbox" checked={!!showAnchorRegion} onChange={onToggleAnchorRegion} />
            <span>Show anchor and region</span>
          </label>
          <label className="toggle-label" title="Show/hide advanced options">
            <input type="checkbox" checked={showAdvanced} onChange={onToggleAdvanced} />
            <span>Advanced</span>
          </label>
          {showAdvanced && (
            <>
              <span className="toggle-separator">|</span>
              {/* "Region zones", not "Regions": the toggle above shows the
                  detected regions, which are not zones. */}
              <label className="toggle-label toggle-label-sm" title="Show field zones">
                <input type="checkbox" checked={showZoneTypeField} onChange={onToggleZoneTypeField} />
                <span>Fields</span>
              </label>
              <label className="toggle-label toggle-label-sm" title="Show anchor zones">
                <input type="checkbox" checked={showZoneTypeAnchor} onChange={onToggleZoneTypeAnchor} />
                <span>Anchors</span>
              </label>
              <label className="toggle-label toggle-label-sm" title="Show region zones">
                <input type="checkbox" checked={showZoneTypeRegion} onChange={onToggleZoneTypeRegion} />
                <span>Region zones</span>
              </label>
              <label className="toggle-label toggle-label-sm" title="Show line item table zones">
                <input type="checkbox" checked={showZoneTypeTable} onChange={onToggleZoneTypeTable} />
                <span>Table zones</span>
              </label>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};

export default EditorHeader;
